import { api } from "@/api/client";
import { convertToURLSearchParams } from "@/util";
import { PaginatedSearch } from "@/types";
import { Project } from "../project";
import { QualifierUser } from "../user";
import { StandardTest } from "./tests";
import { Welder } from "./welders";

export enum TestResult {
  None,
  Pass,
  Fail,
  NotConducted,
}

export enum TestEventStatus {
  New,
  Partial,
  Initiated,
  NoTestResults,
  PartialTestResults,
  Completed,
  Old,
  OldWithTestResults,
  Cancelled,
}

export type TestEvent = {
  id: string;
  number: string;
  description: string;
  status: TestEventStatus;
  project?: Project;
  testEventDate?: string;
  owner?: QualifierUser;
  contractorId?: string;
  supervisor?: string;
  weldingSpecialistId?: string;
  tests: StandardTest[];
  welders: Welder[];
  welderTests: WelderTest[];
  locked: boolean;
};

export type WelderTest = {
  testEventWelderTestId: string;
  testEventId: string;
  welderId: string;
  templateId: string;
  submitted: boolean;
};

export type TestEventListView = {
  id: string;
  number: string;
  description: string;
  projectId: string;
  projectNumber: string;
  status: TestEventStatus;
  owner: string;
  weldingSpecialist: string;
  pinned: boolean;
};

export interface TestEventFilters extends PaginatedSearch {
  favorite: boolean;
  owned: boolean;
  tier2: boolean;
  incomplete: boolean;
  projectId: string;
  welderId: string;
  contractorId: string;
  searchString: string;
}

export type TestEventResults = {
  testEvent: TestEvent;
  welders: WelderResults[];
};

export type WelderResults = {
  welderId: string;
  welderName: string;
  templates: TemplateResults[];
};

export type TemplateResults = {
  recordId: string;
  templateNumber: string;
  standard: number;
  result: TestResult;
  segments: SegmentResults[];
  submitted: boolean;
};

export type SegmentResults = {
  segmentId?: string;
  segmentNumber?: number;
  testDate?: string;
  result: TestResult;
  comment?: string;
  tests: TestResults[];
};

export type TestResults = {
  testResultId: string;
  testId: string;
  testType: string;
  acceptanceCriteria: string;
  result: TestResult;
};

export function getTestEvents(filters: Partial<TestEventFilters>) {
  const params = convertToURLSearchParams(filters);
  return api.getPaginated<TestEventListView[]>(`williams/events?${params}`);
}

export function getTestEventById(id: string) {
  return api.get<TestEvent>(`williams/events/${id}`);
}

export function getTestEventResults(id: string) {
  return api.get<TestEventResults>(`williams/events/results/${id}`);
}

export function getDefaultValues() {
  return api.get<TestEvent>("williams/events/default");
}

export function saveTestEvent(event: TestEvent) {
  return api.post<TestEvent>("williams/events", event);
}

export function saveTestResults(results: TestEventResults) {
  return api.post<TestEventResults>("williams/events/results", results);
}

export function submitTestResults(results: TestEventResults) {
  return api.post<TestEventResults>("williams/events/results/submit", results);
}

export function togglePinned(id: string) {
  return api.post(`williams/events/pin?id=${id}`);
}

export function cancelTestEvent(event: TestEvent) {
  return api.post<TestEvent>("williams/events/cancel", event);
}

export function deleteTestEvent(id: string) {
  return api.delete(`williams/events/${id}`);
}

export function getAdvisories(event: TestEvent) {
  return api.post<string[]>("williams/events/advisories", event);
}
